/**
 * «Матеріали до уроку» — PDF та інші файли під відео уроку (owner 2026-10-21).
 *
 * Той самий рядок-картка, що й `FounderInstagramCard`: плитка 40×40 з
 * іконкою, назва, підпис і стрілка. Файл відкриваємо так само, як файли
 * вакансій (`VacancyMediaBlock`): ПІДПИСАНИМ посиланням через `openLink`, і
 * переглядач Telegram сам малює PDF. Заголовка з initData переглядач не шле,
 * тому доступ тримає підпис у посиланні, яке видає лише список уроків.
 *
 * Один файл — заголовок «Матеріали до уроку», під ним «PDF · 2,4 МБ». Кілька
 * файлів — рядок на кожен, заголовком стає назва файла.
 */
import type { KBLessonMaterialOut } from "@/api/types";
import { Icon } from "@/components/Icon";
import { Section } from "@/components/Section";
import { tg } from "@/lib/telegram";

const TITLE = "Матеріали до уроку";

/** «2,4 МБ», «820 КБ» — десяткова кома, пробіл перед одиницею. */
export function materialSizeLabel(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return "";
  const kb = bytes / 1024;
  if (kb < 1) return "менше 1 КБ";
  if (kb < 1024) return `${Math.round(kb)} КБ`;
  const mb = kb / 1024;
  if (mb < 10) return `${mb.toFixed(1).replace(".", ",").replace(/,0$/, "")} МБ`;
  return `${Math.round(mb)} МБ`;
}

/** Короткий тип файла: «PDF», «Документ», «Таблиця», «Презентація». */
export function materialTypeLabel(m: Pick<KBLessonMaterialOut, "name" | "mime">): string {
  const mime = (m.mime || "").toLowerCase();
  if (mime === "application/pdf") return "PDF";
  if (/spreadsheet|ms-excel|csv/.test(mime)) return "Таблиця";
  if (/presentation|ms-powerpoint/.test(mime)) return "Презентація";
  if (/wordprocessing|msword|opendocument\.text|rtf/.test(mime)) return "Документ";
  if (mime.startsWith("image/")) return "Зображення";
  if (mime.startsWith("text/")) return "Текст";
  return "Файл";
}

function openMaterial(m: KBLessonMaterialOut) {
  tg()?.HapticFeedback?.impactOccurred?.("light");
  const url = `${window.location.origin}${m.url}`;
  const webapp = tg();
  if (webapp?.openLink) webapp.openLink(url);
  else window.open(url, "_blank", "noopener");
}

export function LessonMaterialsCard({
  materials,
}: {
  materials: KBLessonMaterialOut[] | undefined;
}) {
  if (!materials?.length) return null;
  const single = materials.length === 1;

  return (
    <Section bodyClassName="p-0">
      {materials.map((m, i) => (
        <button
          key={`${i}-${m.url}`}
          type="button"
          onClick={() => openMaterial(m)}
          className="flex w-full items-center gap-3 border-b border-line p-4 text-left last:border-b-0"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-primary/10 text-primary">
            <Icon name="materials" size={20} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-display text-[0.9375rem] font-bold text-ink">
              {single ? TITLE : m.name}
            </span>
            <span className="block truncate text-[0.8125rem] text-ink-dim">
              {[materialTypeLabel(m), materialSizeLabel(m.size)]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
          <Icon name="arrow-right" size={14} color="#A8A29C" />
        </button>
      ))}
    </Section>
  );
}
